import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPublicRecipes,
  getUserRecipes,
  getUserSharedApps,
  createRecipe,
  deleteRecipe,
  duplicateRecipe,
  renameRecipe,
  changeOwner,
} from '@/api/recipes';
import { FOLDER_CONTENTS_QUERY_KEY } from '@/queries/useFolders';
import { goToRecipe } from '@/utils/recipes';
import { RecipeType } from '@/enums/recipe-type.enum';
import type { DashboardRecipe } from '@/types/recipes';
import type { FolderScope } from '@/enums/folder-scope.enum';
import type { ListFolderResponseDto } from '@/types/folder.types';

const USER_RECIPES_QUERY_KEY = 'userRecipes';
const PUBLIC_RECIPES_QUERY_KEY = 'publicRecipes';
const USER_SHARED_APPS_QUERY_KEY = 'userSharedApps';

export const useGetUserRecipes = () => {
  return useQuery({
    queryKey: [USER_RECIPES_QUERY_KEY],
    queryFn: getUserRecipes,
  });
};

export const useGetPublicRecipes = () => {
  return useQuery({
    queryKey: [PUBLIC_RECIPES_QUERY_KEY],
    queryFn: getPublicRecipes,
  });
};

export const useGetUserSharedApps = () => {
  return useQuery({
    queryKey: [USER_SHARED_APPS_QUERY_KEY],
    queryFn: getUserSharedApps,
  });
};

export function useCreateRecipe() {
  const qc = useQueryClient();

  return useMutation<
    DashboardRecipe,
    Error,
    { openInNewTab?: boolean; parentId?: string | null; scope?: FolderScope } | void
  >({
    mutationFn: (variables) => createRecipe(variables?.parentId, variables?.scope),

    onSuccess: (data, variables) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev; // avoid dupes
        return [...prev, data];
      });
      goToRecipe(data.id, variables?.openInNewTab, true);
    },
  });
}

export function useDuplicateRecipe() {
  const qc = useQueryClient();

  return useMutation<DashboardRecipe, Error, string>({
    mutationFn: duplicateRecipe,
    onSuccess: (data) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        const prev = old ?? [];
        if (prev.some((i) => i.id === data.id)) return prev; // avoid dupes
        return [...prev, data];
      });

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

export function useDeleteRecipe() {
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string; type: RecipeType }>({
    mutationFn: (variables) => deleteRecipe(variables.id, variables.type !== RecipeType.File),

    onSuccess: (_, variables) => {
      if (variables.type === RecipeType.File) {
        qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
          return old?.filter((recipe) => recipe.id !== variables.id) ?? [];
        });
      } else {
        qc.setQueryData<{ sharedApps: DashboardRecipe[]; sharedWithCurrentUser: DashboardRecipe[] }>(
          [USER_SHARED_APPS_QUERY_KEY],
          (old) => {
            if (!old) return { sharedApps: [], sharedWithCurrentUser: [] };
            return {
              ...old,
              sharedApps: old.sharedApps.filter((recipe) => recipe.id !== variables.id),
            };
          },
        );
      }

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

export function useRenameRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (variables: { id: string; newName: string }) => renameRecipe(variables.id, variables.newName),

    onMutate: (variables) => {
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        return old?.map((r) => (r.id === variables.id ? { ...r, name: variables.newName } : r)) ?? [];
      });

      qc.setQueriesData<ListFolderResponseDto>({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          recipes: old.recipes.map((r) => (r.id === variables.id ? { ...r, name: variables.newName } : r)),
        };
      });
    },

    onError: () => {
      void qc.invalidateQueries({ queryKey: [USER_RECIPES_QUERY_KEY] });
      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}

export function useChangeRecipeOwner() {
  const qc = useQueryClient();

  return useMutation<void, Error, { id: string; newOwnerId: string; newOwnerName?: string }>({
    mutationFn: (variables) => changeOwner(variables.id, variables.newOwnerId),

    onSuccess: (_, variables) => {
      // Update the recipe in userRecipes with new owner
      qc.setQueryData<DashboardRecipe[]>([USER_RECIPES_QUERY_KEY], (old) => {
        return (
          old?.map((r) =>
            r.id === variables.id ? { ...r, owner: variables.newOwnerId, ownerName: variables.newOwnerName } : r,
          ) ?? []
        );
      });

      void qc.invalidateQueries({ queryKey: [FOLDER_CONTENTS_QUERY_KEY] });
    },
  });
}
